
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Variation } from "@/components/admin/locations/types/variation";
import { useColorSchemeCache } from "./hooks/useColorSchemeCache";
import { useIsMobile } from "@/hooks/use-mobile";

interface ProgramColorLegendProps {
  variations: Variation[];
  currentFilter?: string;
  onFilterChange?: (variationKey: string | undefined) => void;
  inline?: boolean;
}

export const ProgramColorLegend: React.FC<ProgramColorLegendProps> = ({ 
  variations = [], 
  currentFilter,
  onFilterChange,
  inline = false
}) => {
  // Use the shared color scheme hook that the calendar uses
  const { getColorForVariation } = useColorSchemeCache(variations);
  
  // Check if the device is mobile
  const isMobile = useIsMobile();
  
  // Ensure variations is always an array
  const safeVariations = Array.isArray(variations) ? variations : [];
  
  // If there's only 0 or 1 variation, don't show the legend at all
  // Also don't show on mobile devices
  if (safeVariations.length <= 1 || isMobile) {
    return null;
  }
  
  // Map color names to Tailwind classes
  const getColorClass = (colorScheme: string): string => {
    const colorMap: Record<string, string> = {
      blue: "bg-blue-500",
      green: "bg-green-500",
      purple: "bg-purple-500",
      orange: "bg-orange-500",
      pink: "bg-pink-500",
      red: "bg-red-500",
      yellow: "bg-yellow-500",
      indigo: "bg-indigo-500",
      teal: "bg-teal-500",
      cyan: "bg-cyan-500",
      gray: "bg-gray-400"
    };
    
    return colorMap[colorScheme] || colorMap.blue;
  };

  if (inline) {
    return (
      <div className="flex items-center">
        <span className="font-medium mr-2">Programs:</span>
        <div className="flex flex-wrap gap-2">
          {onFilterChange && (
            <button 
              className={`flex items-center px-2 py-1 rounded-full text-xs border ${
                !currentFilter ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 hover:bg-gray-100'
              }`}
              onClick={() => onFilterChange(undefined)}
            >
              <span className={`h-3 w-3 rounded-full mr-2`} style={{
                background: "linear-gradient(90deg, #f87171, #facc15, #4ade80, #60a5fa, #a78bfa, #f472b6)"
              }}></span>
              <span>All</span>
            </button>
          )}
          
          {safeVariations.map((variation) => {
            const isSelected = currentFilter === variation.key;
            
            // Get color for this variation using the SAME logic as in the calendar
            const colorScheme = getColorForVariation(variation.key);
            const colorClass = getColorClass(colorScheme);
            
            return (
              <button 
                key={variation.key} 
                className={`flex items-center px-2 py-1 rounded-full text-xs border ${
                  isSelected ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 hover:bg-gray-100'
                }`}
                onClick={() => onFilterChange?.(isSelected ? undefined : variation.key)}
              >
                <span className={`h-3 w-3 rounded-full ${colorClass} mr-2`}></span>
                <span>{variation.displayTitle || variation.key}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardContent className="p-3">
        <h4 className="text-sm font-medium mb-2">Programs</h4>
        <div className="flex flex-wrap gap-2">
          {onFilterChange && (
            <button 
              className={`flex items-center px-2 py-1 rounded-full text-xs border ${
                !currentFilter ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 hover:bg-gray-100'
              }`}
              onClick={() => onFilterChange(undefined)}
            >
              <span className={`h-3 w-3 rounded-full mr-2`} style={{
                background: "linear-gradient(90deg, #f87171, #facc15, #4ade80, #60a5fa, #a78bfa, #f472b6)"
              }}></span>
              <span>All</span>
            </button>
          )}
          
          {safeVariations.map((variation) => {
            const isSelected = currentFilter === variation.key;
            
            // Get color for this variation using the SAME logic as in the calendar
            const colorScheme = getColorForVariation(variation.key);
            const colorClass = getColorClass(colorScheme);
            
            return (
              <button 
                key={variation.key} 
                className={`flex items-center px-2 py-1 rounded-full text-xs border ${
                  isSelected ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 hover:bg-gray-100'
                }`}
                onClick={() => onFilterChange?.(isSelected ? undefined : variation.key)}
              >
                <span className={`h-3 w-3 rounded-full ${colorClass} mr-2`}></span>
                <span>{variation.displayTitle || variation.key}</span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
